import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ModuleGenerationService } from '@/services/ModuleGenerationService';
import { toast } from 'sonner';

const ModuleGeneration: React.FC = () => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [stats, setStats] = useState<{
    totalIncomplete: number;
    withContent: number;
    withoutContent: number;
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  React.useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const result = await ModuleGenerationService.getIncompleteCourseStats();
      setStats({
        totalIncomplete: result.totalIncomplete,
        withContent: result.withContent,
        withoutContent: result.withoutContent
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };
  
  const handleGenerateAll = async () => {
    setGenerating(true);
    setProgress('Starting module generation...');
    
    try {
      const result = await ModuleGenerationService.generateModulesForAllIncompleteCourses();
      
      setProgress(
        `✅ Complete!\n` +
        `Processed: ${result.totalProcessed} courses\n` +
        `Created: ${result.totalModulesCreated} modules\n` +
        `Failures: ${result.failures.length}`
      );
      
      if (result.failures.length > 0) {
        console.error('Failed courses:', result.failures);
      }
      
      toast.success(`Generated ${result.totalModulesCreated} modules for ${result.totalProcessed} courses!`);
      
      // Reload stats after generation
      await loadStats();
      
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate modules');
      setProgress('❌ Generation failed');
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Module Generation System</h1>
      
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">
          Automated Module Generation
        </h2>
        
        {loadingStats ? (
          <p className="mb-4 text-muted-foreground">Loading course statistics...</p>
        ) : stats ? (
          <div className="mb-4 space-y-2">
            <p className="text-muted-foreground">
              Found <span className="font-semibold text-foreground">{stats.totalIncomplete} courses</span> with less than 3 modules:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
              <li>
                <span className="font-semibold text-green-600">{stats.withContent} courses</span> ready (with sufficient content)
              </li>
              <li>
                <span className="font-semibold text-amber-600">{stats.withoutContent} courses</span> will be skipped (insufficient content)
              </li>
            </ul>
          </div>
        ) : (
          <p className="mb-4 text-muted-foreground">
            This will process all courses with less than 3 modules
          </p>
        )}
        
        <p className="mb-4 text-sm text-muted-foreground">
          The system will automatically:
        </p>
        
        <ul className="list-disc ml-6 mb-6 space-y-2">
          <li>Split large content blocks into 4-6 logical modules</li>
          <li>Generate quiz questions for each module</li>
          <li>Create practice exercises</li>
          <li>Insert modules into database</li>
        </ul>
        
        <Button 
          onClick={handleGenerateAll}
          disabled={generating}
          size="lg"
          className="w-full"
        >
          {generating ? 'Generating Modules...' : 'Generate All Course Modules'}
        </Button>
        
        {progress && (
          <pre className="mt-4 p-4 bg-muted rounded-md whitespace-pre-wrap">
            {progress}
          </pre>
        )}
      </Card>
    </div>
  );
};

export default ModuleGeneration;
